import { Briefcase, Beef, Tractor, HardHat, Plane, Droplets } from "lucide-react";

export const COMMON_SECTIONS = [
  {
    id: "applicant",
    title: "Applicant Information",
    fields: [
      { key: "full_name", label: "Full Name", required: true },
      { key: "mobile", label: "Mobile Number", required: true },
      { key: "email", label: "Email" },
      { key: "dob", label: "Date of Birth", type: "date" },
      { key: "gender", label: "Gender", type: "select", options: ["Male", "Female", "Other"] },
      { key: "aadhaar", label: "Aadhaar Number" },
      { key: "pan", label: "PAN" },
    ],
  },
  {
    id: "address",
    title: "Address Information",
    fields: [
      { key: "address", label: "Address", type: "textarea", full: true },
      { key: "state", label: "State", required: true },
      { key: "district", label: "District" },
      { key: "taluk", label: "Taluk / Mandal" },
      { key: "village", label: "Village" },
      { key: "pincode", label: "PIN Code" },
    ],
  },
  {
    id: "bank",
    title: "Bank Information",
    fields: [
      { key: "bank_name", label: "Bank Name" },
      { key: "account_holder", label: "Account Holder Name" },
      { key: "account_number", label: "Account Number" },
      { key: "ifsc", label: "IFSC Code" },
    ],
  },
];

export const SCHEMES = {
  PMEGP: {
    code: "PMEGP",
    name: "PMEGP – Prime Minister's Employment Generation Programme",
    short: "Financial support for starting new micro-enterprises.",
    icon: Briefcase,
    tint: "#eaf5ee", color: "#1f6b3b",
    section: {
      id: "scheme_data", title: "Enterprise & Project Details",
      fields: [
        { key: "enterprise_name", label: "Enterprise Name", required: true },
        { key: "business_activity", label: "Business Activity", required: true },
        { key: "project_cost", label: "Project Cost (₹)", type: "number", required: true },
        { key: "own_contribution", label: "Own Contribution (₹)", type: "number" },
        { key: "loan_required", label: "Loan Required", type: "select", options: ["Yes", "No"] },
        { key: "loan_amount", label: "Loan Amount (₹)", type: "number" },
        { key: "employment_proposed", label: "Employment Proposed (persons)", type: "number" },
        { key: "category", label: "Category", type: "select", options: ["General", "OBC", "SC", "ST", "Other"] },
        { key: "area_type", label: "Area", type: "select", options: ["Rural", "Urban"] },
        { key: "project_description", label: "Project Description", type: "textarea", full: true },
      ],
    },
    documents: [
      { key: "aadhaar", label: "Aadhaar", required: true }, { key: "pan", label: "PAN", required: true },
      { key: "photo", label: "Passport-size Photo", required: true }, { key: "bank_passbook", label: "Bank Passbook", required: true },
      { key: "project_report", label: "Project Report / DPR", required: true }, { key: "category_certificate", label: "Category Certificate" },
      { key: "rural_certificate", label: "Rural-area Certificate" }, { key: "education_certificate", label: "Education/Skill Certificate" },
    ],
  },
  NLM: {
    code: "NLM",
    name: "NLM – National Livestock Mission",
    short: "Entrepreneurship in livestock, poultry, sheep/goat & fodder.",
    icon: Beef,
    tint: "#fdf3e0", color: "#c9821f",
    section: {
      id: "scheme_data", title: "Livestock Project Details",
      fields: [
        { key: "project_type", label: "Project Type", required: true },
        { key: "livestock_activity", label: "Livestock Activity", type: "select", options: ["Sheep/Goat", "Poultry", "Piggery", "Feed/Fodder", "Dairy", "Other"], required: true },
        { key: "new_or_expansion", label: "New / Expansion", type: "select", options: ["New", "Expansion"] },
        { key: "land_details", label: "Land Details" },
        { key: "project_cost", label: "Project Cost (₹)", type: "number", required: true },
        { key: "own_contribution", label: "Own Contribution (₹)", type: "number" },
        { key: "finance_required", label: "Finance Required (₹)", type: "number" },
        { key: "project_description", label: "Project Description", type: "textarea", full: true },
      ],
    },
    documents: [
      { key: "aadhaar", label: "Aadhaar / PAN", required: true }, { key: "bank_details", label: "Bank Details", required: true },
      { key: "land_document", label: "Land Ownership / Lease", required: true }, { key: "project_report", label: "Project Report / DPR", required: true },
      { key: "quotations", label: "Quotations & Cost Details", required: true }, { key: "training_docs", label: "Training / Experience Docs" },
      { key: "entity_registration", label: "Entity Registration (SHG/FPO/JLG)" },
    ],
  },
  SMAM: {
    code: "SMAM",
    name: "SMAM – Sub-Mission on Agricultural Mechanization",
    short: "Subsidy for modern agricultural machinery & Custom Hiring Centres.",
    icon: Tractor,
    tint: "#e6f0fb", color: "#2563eb",
    section: {
      id: "scheme_data", title: "Machinery & Farmer Details",
      fields: [
        { key: "farmer_category", label: "Farmer Category", type: "select", options: ["General", "OBC", "SC", "ST", "Women", "Small/Marginal"] },
        { key: "land_details", label: "Land Details (acres)", required: true },
        { key: "machinery_type", label: "Machinery Type", required: true },
        { key: "equipment_details", label: "Equipment Details" },
        { key: "quotation_amount", label: "Quotation Amount (₹)", type: "number", required: true },
        { key: "chc_applicable", label: "Custom Hiring Centre?", type: "select", options: ["Yes", "No"] },
        { key: "chc_details", label: "CHC Details (if applicable)", type: "textarea", full: true },
      ],
    },
    documents: [
      { key: "aadhaar", label: "Aadhaar / e-KYC", required: true }, { key: "bank_details", label: "Bank Details", required: true },
      { key: "farmer_land_details", label: "Farmer / Land Details", required: true }, { key: "machinery_quotation", label: "Machinery Quotation", required: true },
      { key: "category_certificate", label: "Category Certificate" }, { key: "chc_documents", label: "CHC Project Documents" },
    ],
  },
  VBGR: {
    code: "VBGR",
    name: "VB-G RAM-G",
    short: "Rural wage employment & durable rural assets.",
    icon: HardHat,
    tint: "#f3e8ff", color: "#7c3aed",
    section: {
      id: "scheme_data", title: "Work & Household Details",
      fields: [
        { key: "job_card_number", label: "Job Card Number" },
        { key: "household_details", label: "Household Details", required: true },
        { key: "gram_panchayat", label: "Gram Panchayat", required: true },
        { key: "work_requirement", label: "Work Requirement (days)", type: "number" },
        { key: "work_location", label: "Work Location" },
        { key: "work_type", label: "Work Type", type: "select", options: ["Water Conservation", "Rural Infrastructure", "Agriculture", "Animal Husbandry", "Other"] },
        { key: "work_proposal", label: "Work Proposal", type: "textarea", full: true },
        { key: "agri_ah_details", label: "Agriculture / Animal-Husbandry Details", type: "textarea", full: true },
      ],
    },
    documents: [
      { key: "job_card", label: "Job Card / Household Registration", required: true }, { key: "aadhaar", label: "Aadhaar / Worker ID", required: true },
      { key: "bank_details", label: "Bank Details", required: true }, { key: "gp_work_proposal", label: "Gram Panchayat Work Proposal", required: true },
      { key: "technical_approval", label: "Technical / Approval Documents" },
    ],
  },
  NDD: {
    code: "NDD",
    name: "Namo Drone Didi Scheme",
    short: "Women SHGs providing agricultural drone services (80% CFA up to ₹8L).",
    icon: Plane,
    tint: "#eaf5ee", color: "#1f6b3b",
    section: {
      id: "scheme_data", title: "SHG & Drone Project Details",
      fields: [
        { key: "shg_name", label: "SHG Name", required: true },
        { key: "day_nrlm_id", label: "DAY-NRLM ID", required: true },
        { key: "member_details", label: "Member Details", type: "textarea", full: true },
        { key: "drone_service_details", label: "Drone Service / Project Details", type: "textarea", full: true },
        { key: "project_cost", label: "Project Cost (₹)", type: "number", required: true },
        { key: "financial_assistance", label: "Financial Assistance Sought (₹)", type: "number" },
        { key: "loan_aif_details", label: "Loan / AIF Details (if financing)" },
      ],
    },
    documents: [
      { key: "shg_registration", label: "SHG Registration / DAY-NRLM Records", required: true }, { key: "shg_bank", label: "SHG Bank Details", required: true },
      { key: "member_kyc", label: "Member KYC", required: true }, { key: "selection_recommendation", label: "Selection / Recommendation Docs", required: true },
      { key: "training_certification", label: "Training / Certification Docs" }, { key: "loan_aif", label: "Loan / AIF Documents" },
    ],
  },
  PMKSY: {
    code: "PMKSY",
    name: "PMKSY – Pradhan Mantri Krishi Sinchayee Yojana",
    short: "Micro-irrigation (Per Drop More Crop), water harvesting & watershed support.",
    icon: Droplets,
    tint: "#e0f2fe", color: "#0284c7",
    section: {
      id: "scheme_data", title: "Irrigation, Land & Crop Details",
      fields: [
        { key: "fathers_husbands_name", label: "Father's / Husband's Name" },
        { key: "age", label: "Age", type: "number" },
        { key: "gram_panchayat", label: "Gram Panchayat" },
        { key: "farmer_id", label: "Farmer ID / Registration ID" },
        { key: "pm_kisan_id", label: "PM-KISAN ID (if applicable)" },
        { key: "survey_number", label: "Survey Number", required: true },
        { key: "land_area", label: "Land Area (acres)", type: "number", required: true },
        { key: "cultivable_area", label: "Cultivable Area (acres)", type: "number" },
        { key: "land_ownership", label: "Land Ownership Status", type: "select", options: ["Owned", "Leased", "Joint", "Other"] },
        { key: "land_type", label: "Type of Land" },
        { key: "irrigated_rainfed", label: "Irrigated / Rain-fed Land", type: "select", options: ["Irrigated", "Rain-fed"] },
        { key: "current_irrigation_source", label: "Current Irrigation Source" },
        { key: "existing_irrigation_system", label: "Existing Irrigation System" },
        { key: "irrigation_activity", label: "Proposed Irrigation Activity", type: "select", required: true, options: ["Drip Irrigation", "Sprinkler Irrigation", "Mini Sprinkler", "Micro Irrigation", "Farm Pond / Water Harvesting", "Rainwater Harvesting", "Other eligible irrigation activity"] },
        { key: "main_crop", label: "Main Crop", required: true },
        { key: "crop_variety", label: "Crop Variety" },
        { key: "total_crop_area", label: "Total Crop Area (acres)", type: "number" },
        { key: "season", label: "Season", type: "select", options: ["Kharif", "Rabi", "Summer"] },
        { key: "proposed_irrigation_area", label: "Proposed Irrigation Area (acres)", type: "number" },
        { key: "water_requirement", label: "Water Requirement" },
        { key: "existing_irrigation_method", label: "Existing Irrigation Method" },
        { key: "bank_branch", label: "Bank Branch" },
      ],
    },
    documents: [
      { key: "aadhaar", label: "Aadhaar", required: true }, { key: "pan", label: "PAN" },
      { key: "bank_passbook", label: "Bank Passbook", required: true }, { key: "land_records", label: "Land Records / Survey (RTC / 7-12)", required: true },
      { key: "photo", label: "Passport-size Photo" }, { key: "pm_kisan_proof", label: "PM-KISAN Proof (if applicable)" },
      { key: "irrigation_quotation", label: "Irrigation Equipment Quotation" }, { key: "category_certificate", label: "Category Certificate" },
    ],
  },
};

export const SCHEME_LIST = Object.values(SCHEMES);
